import { CommonModule } from '@angular/common'
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core'

export interface RepaymentReportMetrics {
  sum: number
  count: number
}

export interface RepaymentReportTableRow {
  /** The created date or calendar month used for this aggregate. */
  createdDate: string
  values: Record<string, RepaymentReportMetrics>
}

export interface RepaymentReportTableGroup {
  /** Undefined for the monthly roll-up, which has no repayment-date grouping. */
  repaymentDate?: string
  rows: RepaymentReportTableRow[]
}

export interface RepaymentReportCellSelection {
  source: string
  repaymentDate?: string
  createdDate?: string
  repaymentMonth?: string
}

@Component({
  selector: 'app-repayment-report-table',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './repayment-report-table.component.html',
  styleUrl: './repayment-report-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RepaymentReportTableComponent {
  @Input({ required: true }) groups: RepaymentReportTableGroup[] = []
  @Input({ required: true }) dataSources: string[] = []
  @Input() showRepaymentDate = true
  @Input() dateColumnLabel = 'Created date'
  @Input() loading = false
  @Input() emptyMessage = 'No repayments were found.'
  @Input() selectedCell: RepaymentReportCellSelection | null = null
  @Output() cellSelected = new EventEmitter<RepaymentReportCellSelection>()

  private readonly currencyFormatter = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  private readonly integerFormatter = new Intl.NumberFormat('en-GB', {
    maximumFractionDigits: 0,
  })
  private readonly dateFormatter = new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  })
  private readonly monthFormatter = new Intl.DateTimeFormat('en-GB', {
    month: 'long',
    year: 'numeric',
  })

  rowTotal(row: RepaymentReportTableRow): RepaymentReportMetrics {
    return this.sumMetrics(Object.values(row.values))
  }

  groupTotal(group: RepaymentReportTableGroup): RepaymentReportMetrics {
    return this.sumMetrics(group.rows.map((row) => this.rowTotal(row)))
  }

  sourceTotal(source: string): RepaymentReportMetrics {
    return this.sumMetrics(this.groups.flatMap((group) =>
      group.rows.map((row) => row.values[source] ?? this.emptyMetrics()),
    ))
  }

  sourceTotalForGroup(group: RepaymentReportTableGroup, source: string): RepaymentReportMetrics {
    return this.sumMetrics(group.rows.map((row) => row.values[source] ?? this.emptyMetrics()))
  }

  grandTotal(): RepaymentReportMetrics {
    return this.sumMetrics(this.groups.map((group) => this.groupTotal(group)))
  }

  formatDate(value: string): string {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!match) return value
    return this.dateFormatter.format(new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
  }

  formatMonth(value: string): string {
    const match = value.match(/^(\d{4})-(\d{2})$/)
    if (!match) return value
    return this.monthFormatter.format(new Date(Number(match[1]), Number(match[2]) - 1, 1))
  }

  formatAmount(value: number): string {
    return this.currencyFormatter.format(value)
  }

  formatCount(value: number): string {
    return this.integerFormatter.format(value)
  }

  formatMetricAmount(metrics: RepaymentReportMetrics | undefined): string {
    return (metrics?.count ?? 0) === 0 ? '' : this.formatAmount(metrics?.sum ?? 0)
  }

  formatMetricCount(metrics: RepaymentReportMetrics | undefined): string {
    return (metrics?.count ?? 0) === 0 ? '' : this.formatCount(metrics.count)
  }

  selectCell(group: RepaymentReportTableGroup, row: RepaymentReportTableRow, source: string): void {
    this.cellSelected.emit({
      source,
      repaymentDate: this.showRepaymentDate ? group.repaymentDate : undefined,
      createdDate: this.showRepaymentDate ? row.createdDate : undefined,
      repaymentMonth: this.showRepaymentDate ? undefined : row.createdDate,
    })
  }

  selectSubtotalCell(group: RepaymentReportTableGroup, source: string): void {
    this.cellSelected.emit({ source, repaymentDate: group.repaymentDate })
  }

  isSelectedSubtotalCell(group: RepaymentReportTableGroup, source: string): boolean {
    return this.selectedCell?.source === source
      && this.selectedCell.repaymentDate === group.repaymentDate
      && !this.selectedCell.createdDate
      && !this.selectedCell.repaymentMonth
  }

  isSelectedCell(group: RepaymentReportTableGroup, row: RepaymentReportTableRow, source: string): boolean {
    return this.selectedCell?.source === source
      && this.selectedCell.repaymentDate === (this.showRepaymentDate ? group.repaymentDate : undefined)
      && this.selectedCell.createdDate === (this.showRepaymentDate ? row.createdDate : undefined)
      && this.selectedCell.repaymentMonth === (this.showRepaymentDate ? undefined : row.createdDate)
  }

  private emptyMetrics(): RepaymentReportMetrics {
    return { sum: 0, count: 0 }
  }

  private sumMetrics(values: RepaymentReportMetrics[]): RepaymentReportMetrics {
    return values.reduce(
      (total, value) => ({ sum: total.sum + value.sum, count: total.count + value.count }),
      this.emptyMetrics(),
    )
  }
}
